import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {AbstractContactModel} from '@app/models/abstract-contact.model';
import {GtNameValueService} from '@app/services/gt-name-value.service';
import {GtNotificationService} from '@app/services/gt-notification.service';
import {GtAuthenticationService} from '@app/services/gt-authentication.service';
import {of, Subject} from 'rxjs';
import {GtDirectoryService} from '@app/services/gt-directory.service';
import {GtAbsenceService} from '@app/services/gt-absence.service';
import {GtServiceNumbersUtil} from '@app/shared/utils/gt-service-numbers.util';
import {PhoneNumberFormatter} from '@app/shared/formatting.pipes';
import {StatusAbsentModel} from '@app/models/status-absent.model';
import {absenceActionNameValues, absenceActions} from '@app/models/status.constants';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import {GtPhoneNumberUtil} from '@app/shared/utils/gt-phone-number.util';

@Component({
  selector: 'gt-forwarding-settings',
  templateUrl: './absence-forwarding-settings.component.html',
  styleUrls: ['./absence-forwarding-settings.component.scss']
})
export class AbsenceForwardingSettingsComponent implements OnInit, OnDestroy {
  @Input() login: string;
  @Input() orgname: string;

  @Input()
  status: StatusAbsentModel;

  @Input()
  isActiveAbsence = false;

  @Output()
  backObservable = new EventEmitter<void>();

  @Output()
  configureServiceObservable = new EventEmitter<void>();

  colleagueNumber: string;
  mainNumber: string;
  otherNumber: string;
  mainNumbers: AbstractContactModel[];
  colleagueNumbers: AbstractContactModel[];

  numberTypes = absenceActionNameValues;
  absenceActions = absenceActions;

  get actionId(): number {
    return this.isActiveAbsence ? this.status.actionId : this.status.defaultActionId;
  }

  set actionId(actionId: number) {
    this.status.defaultActionId = actionId;

    if (this.isActiveAbsence) {
      this.status.actionId = actionId;
    }
  }

  private destroyed = new Subject<void>();

  constructor(
    private authenticationService: GtAuthenticationService,
    private directoryService: GtDirectoryService,
    private nameValueService: GtNameValueService,
    private notificationService: GtNotificationService,
    private absenceService: GtAbsenceService,
    private phoneNumberFormatter: PhoneNumberFormatter
  ) {}

  ngOnInit() {
    this.login = this.login ? this.login : this.authenticationService.login;
    this.orgname = this.orgname ? this.orgname : this.authenticationService.oname;
    this.absenceService
      .getAbsenceReasons(this.orgname, this.login)
      .subscribe(x => (this.status.defaultOrganization = x[0] ? x[0].organization : this.status.organization));
    // the absences here needs the owner organization of the current absence agent

    this.directoryService.getServiceNumberModels(this.orgname, false).subscribe(r => {
      this.mainNumbers = r
        .filter(snd => GtServiceNumbersUtil.isMainNumber(snd))
        .sort((a, b) => {
          if (a.name.toLowerCase() < b.name.toLowerCase()) {
            return -1;
          }
          if (a.name.toLowerCase() > b.name.toLowerCase()) {
            return 1;
          }
          return 0;
        });
    }, this.error);

    this.directoryService.getContacts(this.orgname, false).subscribe(r => {
      this.colleagueNumbers = r
        .filter(e => e.isEndUser() && e.login !== this.login)
        .sort((a, b) => {
          if (a.name.toLowerCase() < b.name.toLowerCase()) {
            return -1;
          }
          if (a.name.toLowerCase() > b.name.toLowerCase()) {
            return 1;
          }
          return 0;
        });
    }, this.error);

    this.numberTypes.forEach(s => {
      this.nameValueService.getEndUserNameValue(s.name, this.orgname, this.login).subscribe(r => {
        switch (s.forwardingTarget) {
          case 3:
            this.colleagueNumber = r;
            break;
          case 4:
            this.mainNumber = r;
            break;
          case 5:
            this.otherNumber = this.phoneNumberFormatter.transform(r);
            break;
        }
      }, this.error);
    });
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

  onBack() {
    this.backObservable.emit();
  }

  changeNumber(event?) {
    let value;
    if (event) {
      value = event.value;
    } else {
      value = GtPhoneNumberUtil.toBackend(this.otherNumber);
    }
    this.directoryService
      .getContacts(this.orgname, false)
      .pipe(
        map(contacts => contacts.find(c => c.phoneNumber === value)),
        tap(contact => {
          if (contact?.login === this.login) {
            throw Error('forwarding.error_forward_to_self');
          }
        }),
        switchMap(() =>
          this.nameValueService.setEndUserNameValue(
            this.numberTypes.find(e => e.forwardingTarget === this.actionId).name,
            value,
            this.orgname,
            this.login
          )
        ),
        tap(() => {
          this.status.forwardingNumber = value;
        }),
        catchError((error: Error) => {
          this.notificationService.showErrorCustomMessage(error.message);
          return of();
        })
      )
      .subscribe();
  }

  private error = error => {
    this.notificationService.errorNotification.next(error);
  };

  updateForwardingTypeDefault() {
    switch (this.actionId) {
      case 3:
        this.status.forwardingNumber = this.colleagueNumber;
        break;
      case 4:
        this.status.forwardingNumber = this.mainNumber;
        break;
      case 5:
        this.status.forwardingNumber = this.otherNumber;
        break;
      default:
        this.status.forwardingNumber = '';
        break;
    }
    this.absenceService
      .setAbsenceReasonForUser(this.orgname, this.login, this.status.createAbsenceReason())
      .subscribe(() => {}, this.error);
  }

  configureService() {
    this.configureServiceObservable.emit();
  }
}
